/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ScoreCardQuestionWeightChart from 'components/charts/ScoreCardQuestionWeightChart';
import { fetchHVAScorecardQuestionsData } from 'services/hvaqa.service';
import ScoreEvolutionChart from 'components/charts/QAQuestionScoreEvolutionChart';
import HVASpinner from 'components/base/HVASpinner';
import {
  Card,
  Row,
  Col,
  OverlayTrigger,
  Popover,
  ProgressBar
} from 'react-bootstrap';

interface QuestionData {
  id: number;
  questiontitle: string;
  questiontext: string;
  questionweight: number;
}

interface QAScorecardQuestionsProps {
  questionsData: QuestionData[];
  scorecardid: number;
  startdate: Date;
  enddate: Date;
  serviceid: number;
  campaignid: number;
  agentid: number;
}

interface QuestionItemData {
  averageScore: number | null;
  // eslint-disable-next-line
  evolutionData: any;
}

interface QuestionItemProps {
  question: QuestionData;
  questionData?: QuestionItemData;
  getCardBackgroundClass: (score: number | null) => string;
}

const QAScorecardQuestions: React.FC<QAScorecardQuestionsProps> = React.memo(
  ({
    questionsData,
    scorecardid,
    startdate,
    enddate,
    serviceid,
    campaignid,
    agentid
  }) => {
    const [questionData, setQuestionData] = useState<{
      [key: number]: QuestionItemData;
    }>({});
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const fetchData = useCallback(async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchHVAScorecardQuestionsData({
          scorecardid,
          serviceid,
          campaignid,
          agentid,
          datefrom: startdate,
          dateto: enddate
        });

        const formattedData: { [key: number]: QuestionItemData } = {};
        Object.entries(response).forEach(
          // eslint-disable-next-line
          ([questionId, data]: [string, any]) => {
            formattedData[parseInt(questionId)] = {
              averageScore: data.averageScore
                ? parseFloat(data.averageScore)
                : null,
              evolutionData: data.evolutionData
            };
          }
        );

        setQuestionData(formattedData);
      } catch (err) {
        console.error('Error fetching question data:', err);
        setError('Failed to load question data. Please try again.');
      } finally {
        setLoading(false);
      }
    }, [scorecardid, startdate, enddate, serviceid, campaignid, agentid]);

    useEffect(() => {
      fetchData();
    }, [fetchData]);

    const getCardBackgroundClass = useCallback(
      (score: number | null): string => {
        if (score == null) return 'bg-secondary-light';
        if (score < 6) return 'bg-primary';
        if (score >= 6 && score < 8) return 'bg-warning';
        return 'bg-success';
      },
      []
    );

    const chartData = useMemo(
      () =>
        questionsData.map(question => ({
          value: question.questionweight,
          name: question.questiontitle
        })),
      [questionsData]
    );

    if (loading) return <HVASpinner />;
    if (error) return <div className="text-danger">{error}</div>;

    return (
      <>
        <Row className="mt-2">
          <Col className="d-flex align-items-center justify-content-center w-100">
            <ScoreCardQuestionWeightChart data={chartData} />
          </Col>
        </Row>
        <div className="pt-1">
          {questionsData.map(question => (
            <QuestionItem
              key={question.id}
              question={question}
              questionData={questionData[question.id]}
              getCardBackgroundClass={getCardBackgroundClass}
            />
          ))}
        </div>
      </>
    );
  }
);

const QuestionItem: React.FC<QuestionItemProps> = React.memo(
  ({ question, questionData, getCardBackgroundClass }) => (
    <div>
      <div className="d-flex align-items-center mb-1 p-1">
        <span
          className="d-inline-block bullet-item me-2 bg-primary"
          style={{
            width: '11px',
            height: '11px',
            borderRadius: '50%'
          }}
        />
        <p className="mb-0 fs-9 text-body fw-bold lh-sm flex-1 pt-2">
          {question.questiontitle}
          <br />
          <span className="mb-0 fs-9 text-body fw-normal lh-sm flex-1">
            {question.questiontext}
          </span>
        </p>
        <h6 className="mb-0 text-body">{question.questionweight}%</h6>
      </div>
      <Row className="mb-3 mx-4 me-0 p-0 pt-1 justify-content-start align-items-stretch d-flex">
        <Col
          style={{ width: '100px', flex: '0 0 100px' }}
          className="p-0 me-2 d-flex"
        >
          <OverlayTrigger
            trigger="click"
            placement="top"
            overlay={
              <Popover id="popover" className="shadow-lg">
                <Popover.Body>
                  <div style={{ width: '200px' }}>
                    <p className="lh-sm fs-9">
                      Card background color based on score value:
                    </p>
                    <ProgressBar style={{ height: '15px', width: '100' }}>
                      <ProgressBar
                        style={{
                          backgroundColor: '#c00000'
                        }}
                        now={56}
                        label="0-6"
                        className="lh-1 text-secondary-subtle fw-bold"
                      />
                      <ProgressBar
                        variant="warning"
                        now={22}
                        label="6-8"
                        className="lh-1 text-secondary-subtle fw-bold"
                      />
                      <ProgressBar
                        variant="success"
                        now={22}
                        label="8-10"
                        className="lh-1 text-secondary-subtle fw-bold"
                      />
                    </ProgressBar>
                  </div>
                </Popover.Body>
              </Popover>
            }
          >
            <Card
              className={`text-center pt-0 ${getCardBackgroundClass(
                questionData?.averageScore ?? null
              )}`}
              style={{ width: '185px', height: '100%' }}
            >
              <span className="fs-9 p-0 pt-3 text-secondary-subtle">
                Avg. Score
              </span>
              <h2 className="text-secondary-subtle pb-2">
                {questionData?.averageScore != null
                  ? questionData.averageScore.toFixed(1)
                  : 'N/A'}
              </h2>
            </Card>
          </OverlayTrigger>
        </Col>

        <Col className="px-1 d-flex align-items-stretch">
          {questionData?.evolutionData && (
            <ScoreEvolutionChart data={questionData.evolutionData} />
          )}
        </Col>
      </Row>
    </div>
  )
);

export default QAScorecardQuestions;
